import React from 'react';
import { Box, Chip, FormHelperText, Grid, InputLabel, OutlinedInput, Paper, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const DESCRIPTION_MAX = 300;
const DESCRIPTION_IDEAL_MIN = 50;
const DESCRIPTION_IDEAL_MAX = 160;

interface CustomPageSeoFieldsProps {
    title?: string;
    slug?: string;
    summary?: string;
    metaDescription?: string;
    metaKeywords?: string;
    onChange: (e: any) => void;
}

function descriptionHint(length: number): { text: string; color: 'text.secondary' | 'success.main' | 'warning.main' } {
    if (length === 0) return { text: 'Sem meta descrição, os buscadores usam o resumo da página.', color: 'text.secondary' };
    if (length < DESCRIPTION_IDEAL_MIN) return { text: 'Um pouco curta: o ideal é entre 50 e 160 caracteres.', color: 'warning.main' };
    if (length <= DESCRIPTION_IDEAL_MAX) return { text: 'Tamanho ideal.', color: 'success.main' };
    return { text: 'Acima de 160 caracteres o texto pode ser cortado no resultado da busca.', color: 'warning.main' };
}

export function CustomPageSeoFields({ title, slug, summary, metaDescription, metaKeywords, onChange }: CustomPageSeoFieldsProps) {
    const description = metaDescription || '';
    const hint = descriptionHint(description.length);
    const keywords = (metaKeywords || '').split(',').map((k) => k.trim()).filter(Boolean);
    const previewDescription = description || summary || 'A descrição da página aparecerá aqui.';
    const previewUrl = `${window.location.host}/${slug || 'endereco-gerado-pelo-titulo'}`;

    return (
        <React.Fragment>
            <Grid item xs={12} md={7}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <InputLabel>Meta descrição</InputLabel>
                        <OutlinedInput
                            fullWidth
                            multiline
                            minRows={3}
                            name="metaDescription"
                            value={description}
                            onChange={onChange}
                            inputProps={{ maxLength: DESCRIPTION_MAX }}
                            placeholder="Resumo curto e objetivo do conteúdo da página"
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <FormHelperText sx={{ color: hint.color }}>{hint.text}</FormHelperText>
                            <FormHelperText sx={{ color: hint.color, flexShrink: 0 }}>{description.length}/{DESCRIPTION_IDEAL_MAX}</FormHelperText>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Palavras-chave</InputLabel>
                        <OutlinedInput
                            fullWidth
                            name="metaKeywords"
                            value={metaKeywords || ''}
                            onChange={onChange}
                            placeholder="Ex.: licitações, editais, compras públicas"
                        />
                        <FormHelperText>Separe os termos por vírgula.</FormHelperText>
                        {keywords.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                                {keywords.map((k, index) => <Chip key={`${k}-${index}`} size="small" label={k} variant="outlined" color="primary" />)}
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12} md={5}>
                <InputLabel>Prévia no resultado de busca</InputLabel>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: 'text.secondary' }}>
                        <SearchIcon fontSize="small" />
                        <Typography variant="caption">Como a página pode aparecer no Google</Typography>
                    </Box>
                    <Typography variant="caption" component="div" sx={{ color: '#202124' }} noWrap>{previewUrl}</Typography>
                    <Typography sx={{ color: '#1a0dab', fontSize: '1.1rem', lineHeight: 1.3, my: 0.25 }} noWrap>
                        {title || 'Título da página'}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: '#4d5156', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                        {previewDescription}
                    </Typography>
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
